//* react
import React from 'react';

//* material-ui
import { alpha, Box, darken, lighten, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

//* types
import { TemplatePreviewProps } from 'types/templates/templates-menu';
import { getDateParts, getFileFormatFromURL } from 'utils/helpers';

//* icons
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import VideoFileOutlinedIcon from '@mui/icons-material/VideoFileOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import AddLocationOutlinedIcon from '@mui/icons-material/AddLocationOutlined';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReplyOutlinedIcon from '@mui/icons-material/ReplyOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import TemplateMessage from 'views/chats/singleChat/chat/conversationWindow/messages/single-message/message-types/TemplateMessage';

//* system

const templatePreviewsByFileType = {
  pdf: '/images/file-types/pdf.png',
  docx: '/images/file-types/docx_icon.png',
  xlsx: '/images/file-types/xlsx.png',
  zip: '/images/file-types/zip.png',
};

const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  originalTemplate,
  varsData,
  mediaFile,
  noUpload,
  readyTemplate,
}) => {
  const { templateHeader, templateBody, templateFooter, templateButtons } = React.useMemo(() => {
    const templateHeader = originalTemplate?.components?.find((comp) => comp.type === 'HEADER');
    const templateBody = originalTemplate?.components?.find((comp) => comp.type === 'BODY');
    const templateFooter = originalTemplate?.components?.find((comp) => comp.type === 'FOOTER');
    const templateButtons = originalTemplate?.components?.find((comp) => comp.type === 'BUTTONS');

    return {
      templateHeader,
      templateBody,
      templateFooter,
      templateButtons,
    };
  }, [originalTemplate]);

  const currentDate = React.useMemo(() => {
    const dateParts = getDateParts();
    return `${dateParts.day}/${dateParts.month}/${dateParts.year} ${dateParts.hours}:${dateParts.minutes}`;
  }, []);

  const fileType = React.useMemo(
    () => mediaFile?.url && (getFileFormatFromURL(mediaFile.url) as keyof typeof templatePreviewsByFileType),
    [mediaFile],
  );

  const buttonsContainerRef = React.useRef<HTMLDivElement | null>(null);

  return (
    <Box
      sx={{
        position: 'relative',
        pl: 1.25,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 'calc(100% - 10px)',
          width: 0,
          height: 0,
          borderRight: (theme) =>
            `10px solid ${
              theme.palette.mode === 'dark'
                ? alpha(darken(theme.palette.primary.main, 0.5), 0.65)
                : lighten(theme.palette.primary.main, 0.75)
            }`,
          borderBottom: '15px solid transparent',
        }}
      ></Box>
      <Box
        sx={{
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? alpha(darken(theme.palette.primary.main, 0.5), 0.65)
              : lighten(theme.palette.primary.main, 0.75),
          minWidth: 240,
          maxWidth: 325,
          p: 1.25,
          borderRadius: ' 0 8px 8px 8px',
        }}
      >
        <Grid container spacing={1}>
          {readyTemplate ? (
            <React.Fragment>
              <TemplateMessage
                isIncoming={false}
                text={readyTemplate}
                handleMediaPreview={() => {}}
                status={''}
                scrollToBottom={() => {}}
                mediaShouldScrollBottomRef={{
                  current: false,
                }}
                buttonsContainerRef={buttonsContainerRef}
              />
              <Grid size={12}>
                <Box ref={buttonsContainerRef}></Box>
              </Grid>
            </React.Fragment>
          ) : (
            <React.Fragment>
              {templateHeader && (
                <Grid size={12} position={'relative'}>
                  {templateHeader.format === 'TEXT' ? (
                    <Typography
                      variant="body2"
                      fontSize={14}
                      fontWeight={700}
                      sx={{
                        wordBreak: 'break-all',
                      }}
                    >
                      {varsData?.find((param) => param.name === 'header_1') &&
                      varsData?.find((param) => param.name === 'header_1')?.text !== ''
                        ? templateHeader.text?.replace(
                            '{{1}}',
                            varsData?.find((param) => param.name === 'header_1')?.text || '{{1}}',
                          )
                        : templateHeader.text}
                    </Typography>
                  ) : mediaFile?.url ? (
                    mediaFile.type === 'video' ? (
                      <video
                        src={mediaFile?.url}
                        controls={false}
                        style={{
                          width: '100%',
                          height: 175,
                          background: 'background.paper',
                          objectFit: 'contain',
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: 175,
                          background: 'background.paper',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                        }}
                      >
                        <img
                          src={
                            mediaFile.type === 'image'
                              ? mediaFile.url
                              : templatePreviewsByFileType[fileType || 'docx']
                          }
                          alt=""
                          style={{
                            width: mediaFile.type !== 'image' ? '65%' : '100%',
                            height: mediaFile.type !== 'image' ? '65%' : '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </Box>
                    )
                  ) : (
                    <Box
                      sx={{
                        width: '100%',
                        height: 175,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.25,
                        justifyContent: 'center',
                        color: 'secondary.main',
                      }}
                    >
                      {templateHeader.format === 'IMAGE' ? (
                        <AddPhotoAlternateOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : templateHeader.format === 'VIDEO' ? (
                        <VideoFileOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : templateHeader.format === 'DOCUMENT' ? (
                        <UploadFileOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : templateHeader.format === 'LOCATION' ? (
                        <AddLocationOutlinedIcon sx={{ width: 70, height: 70 }} />
                      ) : (
                        ''
                      )}
                      <Typography fontSize={15} fontWeight={700} width="100%" textAlign={'center'}>
                        העלאת קובץ
                      </Typography>
                    </Box>
                  )}
                  {['IMAGE', 'DOCUMENT', 'VIDEO'].includes(templateHeader.format) && (
                    <Box
                      component={'label'}
                      sx={{
                        pointerEvents: noUpload ? 'none' : undefined,
                        cursor: noUpload ? 'default' : 'pointer',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 100,
                      }}
                      htmlFor="template-header-media-upload"
                    />
                  )}
                </Grid>
              )}
              <Grid size={12}>
                <Typography variant="body2" fontSize={14} whiteSpace={'pre-line'}>
                  {varsData?.some((param) => param.name.startsWith('body'))
                    ? (templateBody?.text || '').replace(/\{\{(\d+)\}\}/g, (_, match) => {
                        const param = varsData.find((param) => param.name === `body_${parseInt(match)}`);
                        return param?.text && param.text !== '' ? param.text : `{{${match}}}`;
                      })
                    : templateBody?.text}
                </Typography>
              </Grid>

              {templateFooter && (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="text.secondary" fontSize={13}>
                    {varsData?.some((param) => param.name.startsWith('footer'))
                      ? (templateFooter?.text || '').replace(/\{\{(\d+)\}\}/g, (_, match) => {
                          const param = varsData.find((param) => param.name === `footer_${parseInt(match)}`);
                          return param?.text && param.text !== '' ? param.text : `{{${match}}}`;
                        })
                      : templateFooter?.text}
                  </Typography>
                </Grid>
              )}
            </React.Fragment>
          )}
          <Grid size={12}>
            <Grid container spacing={1} alignItems={'center'}>
              <Grid>
                <DoneAllIcon
                  sx={{
                    fontSize: 16,
                    color: 'text.secondary',
                    verticalAlign: 'middle',
                  }}
                />
              </Grid>
              <Grid>
                <Typography variant="body2" color={'text.disabled'} fontSize={12}>
                  {currentDate}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {templateButtons && (
            <Grid size={12}>
              {templateButtons.buttons?.map((button, index) => (
                <Box
                  key={index}
                  sx={{
                    mx: -1.25,
                    px: 1.25,
                    borderTop: '1px solid #c2c2c2',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      p: 1,
                      color: 'info.main',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'currentColor',
                        textDecoration: button.type === 'URL' ? 'underline' : 'none',
                      }}
                    >
                      {button.text}
                    </Typography>
                    {button.type === 'QUICK_REPLY' && <ReplyOutlinedIcon />}
                    {button.type === 'URL' && <OpenInNewOutlinedIcon />}
                    {button.type === 'PHONE_NUMBER' && <PhoneOutlinedIcon />}
                    {button.type === 'COPY_CODE' && <CodeOutlinedIcon />}
                  </Box>
                </Box>
              ))}
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default TemplatePreview;
